import "@/App.css";
import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";

import Landing from "./pages/Landing";
import Games from "./pages/Games";
import GamePlay from "./pages/GamePlay";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import WalletPage from "./pages/Wallet";
import Leaderboard from "./pages/Leaderboard";
import Support from "./pages/Support";
import LegalPage from "./pages/LegalPage";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminAppInstall from "./pages/admin/AdminAppInstall";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminGames from "./pages/admin/AdminGames";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminStaffUsers from "./pages/admin/AdminStaffUsers";
import AdminRequests from "./pages/admin/AdminRequests";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminWallet from "./pages/admin/AdminWallet";
import AdminAutomation from "./pages/admin/AdminAutomation";
import AdminGameCredits from "./pages/admin/AdminGameCredits";
import AdminFreeCredits from "./pages/admin/AdminFreeCredits";
import AdminClientReferrals from "./pages/admin/AdminClientReferrals";
import AdminCommunications from "./pages/admin/AdminCommunications";
import AdminPaymentMethods from "./pages/admin/AdminPaymentMethods";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminTelegram from "./pages/admin/AdminTelegram";
import AdminDepositors from "./pages/admin/AdminDepositors";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminRewards from "./pages/admin/AdminRewards";
import AdminPlaceholder from "./pages/admin/AdminPlaceholder";
const AdminPaymentFlows = lazy(() => import("./pages/admin/AdminPaymentFlows"));
import { PaymentsReview, PayoutsReview } from "./pages/admin/AdminReviewQueue";
import InstallPrompt from "./components/InstallPrompt";
import SupportButton from "./components/SupportButton";
import BottomNav from "./components/BottomNav";
import ErrorBoundary from "./components/ErrorBoundary";
import MaintenanceOverlay from "./components/MaintenanceOverlay";
import PlayerRegionGate from "./components/PlayerRegionGate";

function App() {
  const [maintenance, setMaintenance] = useState({ open: false, fatal: false });

  useEffect(() => {
    const updateDeviceLayout = () => {
      const root = document.documentElement;
      const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
      const touchPoints = navigator.maxTouchPoints || 0;
      const screenWidth = window.screen?.width || window.innerWidth;
      const screenHeight = window.screen?.height || window.innerHeight;
      const smallestScreenSide = Math.min(screenWidth, screenHeight);
      const realPhone = (coarsePointer || touchPoints > 0) && smallestScreenSide <= 820;
      root.classList.toggle("sophia-real-phone", realPhone);
    };

    updateDeviceLayout();
    const pointerQuery = window.matchMedia?.("(pointer: coarse)");
    pointerQuery?.addEventListener?.("change", updateDeviceLayout);
    window.addEventListener("resize", updateDeviceLayout);
    window.addEventListener("orientationchange", updateDeviceLayout);

    return () => {
      pointerQuery?.removeEventListener?.("change", updateDeviceLayout);
      window.removeEventListener("resize", updateDeviceLayout);
      window.removeEventListener("orientationchange", updateDeviceLayout);
    };
  }, []);

  useEffect(() => {
    const isAdmin = () => window.location.pathname.startsWith("/admin");

    const openMaintenance = (event) => {
      if (isAdmin()) return;
      setMaintenance({
        open: true,
        fatal: Boolean(event?.detail?.fatal),
      });
    };

    const onUnhandledRejection = (event) => {
      if (event?.reason?.isAxiosError) return;
      const reasonText = `${event?.reason?.message || ""} ${event?.reason?.stack || ""}`;
      if (/posthog|cloudflare|beacon|chrome-extension/i.test(reasonText)) return;
      if (!event?.reason || typeof event.reason !== "object") return;
      // React render failures are handled by ErrorBoundary. Avoid labeling
      // unrelated client-side script issues as website maintenance.
      console.warn("Unhandled client error", event.reason);
    };

    window.addEventListener("sophia:maintenance", openMaintenance);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("sophia:maintenance", openMaintenance);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <PlayerRegionGate>
            <AuthProvider>
            <Toaster theme="dark" position="top-right" richColors />
            <InstallPrompt />
            <SupportButton />
            <BottomNav />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/games" element={<Games />} />
              <Route path="/games/:id" element={<GamePlay />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/support" element={<Support />} />
              <Route path="/:slug" element={<LegalPage />} />
              <Route path="/c/*" element={<Navigate to="/admin/support-app" replace />} />

              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/app" element={<AdminAppInstall />} />
              <Route element={<AdminLayout />}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/plans" element={<AdminPlaceholder title="Plans" />} />
                <Route path="/admin/members" element={<AdminStaffUsers />} />
                <Route path="/admin/requests" element={<AdminRequests />} />
                <Route path="/admin/wallet" element={<AdminWallet />} />
                <Route path="/admin/automation" element={<AdminAutomation />} />
                <Route path="/admin/game-credits" element={<AdminGameCredits />} />
                <Route path="/admin/free-credits" element={<AdminFreeCredits />} />
                <Route path="/admin/client-referrals" element={<AdminClientReferrals />} />
                <Route path="/admin/payment-methods" element={<AdminPaymentMethods />} />
                <Route path="/admin/transactions" element={<AdminTransactions />} />
                <Route path="/admin/payments-review" element={<PaymentsReview />} />
                <Route path="/admin/payouts-review" element={<PayoutsReview />} />
                <Route path="/admin/payment-flows" element={<Suspense fallback={<div className="p-6 text-sm text-slate-400" role="status">Loading flows...</div>}><AdminPaymentFlows /></Suspense>} />
                <Route path="/admin/analytics" element={<AdminAnalytics />} />
                <Route path="/admin/depositors" element={<AdminDepositors />} />
                <Route path="/admin/branding" element={<AdminPlaceholder title="Branding" />} />
                <Route path="/admin/categories" element={<AdminPlaceholder title="Categories" />} />
                <Route path="/admin/communications" element={<AdminCommunications />} />
                <Route path="/admin/chat" element={<AdminSupport />} />
                <Route path="/admin/support-app" element={<AdminSupport appMode />} />
                <Route path="/admin/rewards" element={<AdminRewards />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/activity" element={<AdminPlaceholder title="Activity" />} />
                <Route path="/admin/support" element={<Navigate to="/admin/support-app" replace />} />
                <Route path="/admin/telegram" element={<AdminTelegram />} />
                <Route path="/admin/games" element={<AdminGames />} />
                <Route path="/admin/users" element={<AdminUsers />} />
              </Route>
              <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </AuthProvider>
          </PlayerRegionGate>
        </BrowserRouter>
      </ErrorBoundary>
      <MaintenanceOverlay
        open={maintenance.open}
        fatal={maintenance.fatal}
        onDismiss={() => setMaintenance({ open: false, fatal: false })}
        onRetry={() => window.location.reload()}
      />
    </div>
  );
}

export default App;
